// Perfis DERIVADOS — o lado da TELA da regra que mora em server/perfis-derivados.js.
//
// Em 2026-08-19 a `Diretoria` (5 pessoas) foi segmentada por área: cada diretor
// passou a alcançar só os módulos que lhe dizem respeito. Cada perfil derivado
// declara DE QUEM herda a permissão e EM QUE MÓDULOS ela vale — fora deles, nada.
//
// ⚠️ QUEM PROTEGE O DADO É O BACKEND. Aqui só decidimos o que aparece: item de
//    menu, rota, card, botão. Um esquecimento neste arquivo esconde uma tela de
//    quem podia usá-la — não abre nada para quem não podia.
// ⚠️ ESTE MAPA ESPELHA O DO BACKEND. Divergir faz a tela e o servidor
//    discordarem: o card aparece e a chamada volta 403 (ou o contrário).
//
// COMO USAR — três formas, uma para cada lugar:
//
//   1. Dentro de uma tela DO MÓDULO, traduza o perfil uma vez, na leitura:
//        const role = perfilNoModulo(user.perfil, 'rh');   // 'Marketing' -> 'Diretoria'
//      A partir daí a regra da tela segue escrita em cima de `Diretoria`, sem
//      repetir o nome de cada perfil derivado em cada `if`.
//
//   2. Numa lista de perfis de rota (App.js), some os derivados do módulo:
//        allowedRoles={['RH', 'Admin', 'Diretoria', ...perfisDoModulo('rh')]}
//
//   3. Num teste de menu (Sidebar.js), pergunte pelo alcance:
//        alcancaModulo(userRole, 'financeiro')
//
// ⚠️ NUNCA traduza sem dizer o módulo, e nunca use o módulo errado: é o módulo
//    que separa quem alcança de quem não alcança. `Marketing` no Financeiro
//    continua sendo `Marketing` — ele alcança o módulo por ACESSO TOTAL (bypass,
//    ver abaixo), não por herança, então não vira `Diretoria` nas regras de tela.
// ⚠️ NÃO use para EXIBIR o perfil (cabeçalho, log, relatório): a pessoa é
//    `Marketing`, e mostrar `Diretoria` seria mentir sobre o cadastro.

// perfil derivado -> de quem herda + em que módulos vale (espelha o backend)
export const PERFIS_DERIVADOS = {
    'Diretoria RH': {
        herdaDe: 'Diretoria',
        modulos: ['rh', 'medicina', 'processoseletivo', 'treinamento'],
    },
    // ⚠️ `chamados` entrou em 2026-08-31, junto com a liberação da `Diretoria` no
    //    módulo. Espelha o backend — o teste `espelhos-back-front` reprova divergência.
    // ⚠️ `biblioteca` entrou em 2026-09-04, com o módulo (1º manual: Processos
    //    Financeiros, a rotina de caixa da loja). Espelha o backend.
    // ⚠️ `logistica` entrou em 2026-09-10, com o módulo — e é MANUTENÇÃO de
    //    alcance, não ampliação: a Retirada de Equipamento saiu de `manutencao` e
    //    `chamados` (que ela já alcança) para módulo próprio. Espelha o backend.
    'Diretoria Op': {
        herdaDe: 'Diretoria',
        modulos: [
            'operacional', 'manutencao', 'auditoria', 'financeiro', 'compras',
            'medicina', 'processoseletivo', 'rh', 'treinamento', 'chamados',
            'biblioteca', 'logistica',
            // ⚠️ `marketing` ENTROU EM 18/09/2026, com a abertura do módulo. Ela
            //    vem pelo DERIVADO e não pela lista de perfis do módulo porque é
            //    assim que perfil derivado alcança qualquer coisa aqui —
            //    escrever 'Diretoria Op' à mão lá criaria uma segunda verdade
            //    sobre o recorte dela. `Diretoria RH` segue fora: não foi pedida.
            'marketing',
        ],
    },
    // ⚠️ `Marketing` não é nome novo: o perfil já existia no código do Calendário
    //    da Home (gestão de feriados) sem nunca ter tido dono. Ao ganhar dono,
    //    aqueles comportamentos passaram a valer — foi decisão, não descuido.
    // ⚠️ `marketing` entrou em 2026-09-16, com o módulo — é a casa dele.
    //    Espelha o backend.
    'Marketing': {
        herdaDe: 'Diretoria',
        modulos: ['processoseletivo', 'rh', 'treinamento', 'marketing'],
    },
};

/** Este perfil alcança este módulo? (false para perfil comum — ele não usa herança) */
export function alcancaModulo(perfil, modulo) {
    const cfg = PERFIS_DERIVADOS[perfil];
    return !!cfg && cfg.modulos.includes(modulo);
}

// ---------------------------------------------------------------------------
// ACESSO TOTAL POR MÓDULO — o lado da TELA do SEGUNDO bypass do portal (o back
// mora em server/perfis-derivados.js › ACESSO_TOTAL_POR_MODULO).
//
// Diferente da herança acima, isto é bypass de verdade: o perfil age como Admin
// DENTRO dos módulos listados (vê todos os cards/abas). As exceções de submódulo
// são por ID (o back recorta por PREFIXO de API — duas representações da MESMA
// regra; o teste `espelhos-back-front` reprova divergência).
// ⚠️ QUEM PROTEGE O DADO É O BACKEND. Aqui só decidimos o que aparece.
export const ACESSO_TOTAL_POR_MODULO = {
    'Operacional': {
        modulos: [
            'treinamento', 'medicina', 'manutencao', 'logistica', 'biblioteca',
            'rh', 'financeiro',
        ],
        // IDs de submódulo retirados do total, por hub (espelham os `excetoPrefixos`
        // do backend: /api/rh/controle-experiencia, /api/automacao-rh,
        // /api/automacoes-financeiro, /api/numerario, /api/fita e /api/financeiro/gprs).
        // ⚠️ No Financeiro o Operacional entra em só 5 submódulos (Estorno, Petty
        //    Cash, Troco, Nulos e Vídeos); os 4 abaixo ficam de fora (28/09/2026).
        excetoSubmodulos: {
            rh: ['controle-experiencia', 'automacoes'],
            financeiro: ['automacoes', 'numerario', 'fita', 'gprs', 'cofrinho'],
        },
    },
    // Marketing — acesso TOTAL ao Financeiro, sem recorte (pedido da Julia, 30/09/2026).
    // Espelha o backend (server/perfis-derivados.js › ACESSO_TOTAL_POR_MODULO).
    // ⚠️ É ACESSO TOTAL, não herança: `Marketing` herda de `Diretoria`, mas a maioria
    //    das rotas do Financeiro não lista `Diretoria` — herança daria acesso quebrado
    //    (card aparece, chamada volta 403). O total é o único jeito de dar TODOS os
    //    submódulos. Sem `excetoSubmodulos`: a decisão foi "todos os submódulos".
    'Marketing': {
        modulos: ['financeiro'],
        excetoSubmodulos: {},
    },
};

/** Este perfil tem acesso TOTAL (bypass) a este módulo? (granularidade de módulo) */
export function temAcessoTotal(perfil, modulo) {
    const cfg = ACESSO_TOTAL_POR_MODULO[perfil];
    return !!cfg && !!modulo && cfg.modulos.includes(modulo);
}

/**
 * IDs de submódulo que o acesso total deste perfil NÃO cobre neste módulo — para
 * os hubs recortarem os cards/abas (ex.: RH não mostra Controle de Experiência).
 */
export function submodulosExcluidos(perfil, modulo) {
    return ACESSO_TOTAL_POR_MODULO[perfil]?.excetoSubmodulos?.[modulo] || [];
}

/**
 * Nomes dos perfis com acesso total a este módulo — para somar às listas de
 * `allowedRoles` das rotas (idioma do `perfisDoModulo`). Perfil novo entra sozinho.
 */
export function perfisComAcessoTotal(modulo) {
    return Object.keys(ACESSO_TOTAL_POR_MODULO).filter((p) => temAcessoTotal(p, modulo));
}

/**
 * Perfil a usar nas REGRAS de tela dentro de um módulo.
 * Traduz só quando o perfil derivado alcança AQUELE módulo; nos demais casos
 * (perfil comum, ou derivado fora do recorte) devolve o perfil como está.
 */
export function perfilNoModulo(perfil, modulo) {
    return alcancaModulo(perfil, modulo) ? PERFIS_DERIVADOS[perfil].herdaDe : perfil;
}

/**
 * Nomes dos perfis derivados que alcançam este módulo — para somar às listas de
 * `allowedRoles` das rotas. Derivado novo entra sozinho, sem varrer o App.js.
 */
export function perfisDoModulo(modulo) {
    return Object.keys(PERFIS_DERIVADOS).filter((p) => alcancaModulo(p, modulo));
}
